import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Check, Globe } from "lucide-react";
import { Reveal } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";
import { CASE_STUDIES } from "../data/content";

const BrowserFrame = ({ domain, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-[1.75rem] border border-ink/5 shadow-[0_24px_64px_rgba(20,22,46,0.14)] overflow-hidden">
    <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-ink/5 bg-cloud/70">
      <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
      <span className="ml-3 text-[11px] text-slate bg-white border border-ink/8 rounded-full px-3 py-1">{domain}</span>
    </div>
    {children}
  </div>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const study = CASE_STUDIES.find((s) => s.slug === slug);

  if (!study) {
    return (
      <section className="pt-[72px] min-h-[60vh] flex items-center" data-testid="case-not-found">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h1 className="font-display font-bold text-3xl text-ink mb-4">That case study doesn't exist.</h1>
          <Link to="/work" className="text-indigo font-semibold inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to our work
          </Link>
        </div>
      </section>
    );
  }

  const next = CASE_STUDIES[(CASE_STUDIES.indexOf(study) + 1) % CASE_STUDIES.length];

  return (
    <>
      <section className="pt-[72px] bg-ink grain" data-testid="case-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20 relative z-10">
          <Reveal>
            <Link to="/work" data-testid="case-back-link" className="inline-flex items-center gap-2 text-white/50 text-sm font-medium hover:text-mint transition-colors duration-200 mb-10">
              <ArrowLeft className="w-4 h-4" /> All client work
            </Link>
            <p className="text-mint font-semibold text-sm uppercase tracking-widest mb-4">{study.industry}</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.06] max-w-3xl" data-testid="case-title">
              {study.name}
            </h1>
            <p className="mt-6 text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed">{study.tagline}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {study.services.map((s) => (
                <span key={s} className="text-xs font-medium text-white/85 bg-white/10 border border-white/10 rounded-full px-4 py-1.5">{s}</span>
              ))}
            </div>
            <a
              href={study.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="case-visit-site"
              className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-indigo to-mint text-white font-semibold px-7 py-3.5 rounded-full hover:opacity-90 hover:scale-[1.02] transition-[opacity,transform] duration-300"
            >
              Visit the live site <ExternalLink className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-24" data-testid="case-showcase">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            {study.images ? (
              <BrowserFrame domain={study.domain} testid="case-screenshot-main">
                <img src={study.images[0]} alt={`${study.name} homepage`} className="w-full object-cover" />
              </BrowserFrame>
            ) : (
              <BrowserFrame domain={study.domain} testid="case-live-panel">
                <a href={study.url} target="_blank" rel="noopener noreferrer" className="group block bg-gradient-to-br from-indigo via-[#4549c9] to-mint px-8 py-24 sm:py-32 text-center relative overflow-hidden">
                  <div className="absolute inset-0 grain" />
                  <div className="relative z-10">
                    <Globe className="w-10 h-10 text-white/80 mx-auto mb-6" />
                    <p className="font-display font-bold text-white text-2xl sm:text-3xl mb-3">{study.domain}</p>
                    <p className="text-white/75 text-sm inline-flex items-center gap-2 border border-white/30 rounded-full px-5 py-2.5 group-hover:bg-white group-hover:text-ink transition-colors duration-300">
                      See it live <ExternalLink className="w-3.5 h-3.5" />
                    </p>
                  </div>
                </a>
              </BrowserFrame>
            )}
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-20 sm:pb-24" data-testid="case-story">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">The client</p>
            <p className="text-ink/75 text-base leading-relaxed mb-10">{study.overview}</p>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">The challenge</p>
            <p className="text-ink/75 text-base leading-relaxed">{study.challenge}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm p-8 sm:p-10 h-full">
              <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-6">What we delivered</p>
              <ul className="space-y-4">
                {study.solution.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-ink/80 leading-relaxed">
                    <span className="w-6 h-6 rounded-full bg-mint/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-mint" />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {study.quote && (
        <section className="px-5 sm:px-8 pb-20 sm:pb-24" data-testid="case-quote">
          <Reveal className="max-w-4xl mx-auto text-center">
            <span className="font-display font-bold text-7xl text-gradient leading-none">"</span>
            <blockquote className="font-display font-semibold text-ink text-xl sm:text-2xl leading-snug -mt-4 mb-6">
              {study.quote.text}
            </blockquote>
            <p className="text-sm font-semibold text-ink">{study.quote.author}</p>
            <p className="text-xs text-slate mt-0.5">{study.quote.role}</p>
          </Reveal>
        </section>
      )}

      {study.images && study.images.length > 1 && (
        <section className="px-5 sm:px-8 pb-20 sm:pb-24 space-y-8" data-testid="case-screenshot-two">
          {study.images.slice(1).map((img, i) => (
            <Reveal key={img} className="max-w-5xl mx-auto">
              <BrowserFrame domain={study.domain} testid={`case-screenshot-secondary-${i}`}>
                <img src={img} alt={`${study.name} website section ${i + 2}`} className="w-full object-cover" />
              </BrowserFrame>
            </Reveal>
          ))}
        </section>
      )}

      <section className="px-5 sm:px-8 pb-24" data-testid="case-outcome">
        <Reveal className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-indigo to-mint rounded-[2rem] p-10 sm:p-14 text-center">
            <p className="text-white/80 font-semibold text-sm uppercase tracking-widest mb-4">The outcome</p>
            <p className="font-display font-semibold text-white text-xl sm:text-2xl leading-snug max-w-2xl mx-auto">{study.outcome}</p>
          </div>
        </Reveal>
        <Reveal className="max-w-5xl mx-auto mt-10 flex justify-center">
          <Link
            to={`/work/${next.slug}`}
            data-testid="case-next-link"
            className="group inline-flex items-center gap-3 bg-white border border-ink/8 rounded-full pl-7 pr-5 py-4 shadow-sm hover:shadow-md transition-shadow duration-300"
          >
            <span className="text-sm text-slate">Next project:</span>
            <span className="font-display font-semibold text-ink text-sm">{next.name}</span>
            <ArrowRight className="w-4 h-4 text-indigo transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <CTAPanel title="Want results like these?" sub="Tell us about your business and we'll map out exactly what we'd build for you — free, no strings." />
    </>
  );
}
